import { forwardRef } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ShoppingCart, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { ShopifyProduct, formatPrice } from '@/lib/shopify';
import { useCartStore } from '@/stores/cartStore';
import { toast } from 'sonner';

interface ProductCardProps {
  product: ShopifyProduct;
  index?: number;
}

export const ProductCard = forwardRef<HTMLDivElement, ProductCardProps>(
  ({ product, index = 0 }, ref) => {
    const { t } = useTranslation();
    const addItem = useCartStore(state => state.addItem);
    const isLoading = useCartStore(state => state.isLoading);
    const { node } = product;
    
    const firstImage = node.images.edges[0]?.node;
    const firstVariant = node.variants.edges[0]?.node;
    const price = node.priceRange.minVariantPrice;

    const handleAddToCart = async (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      
      if (!firstVariant) return;
      
      await addItem({
        product,
        variantId: firstVariant.id,
        variantTitle: firstVariant.title,
        price: firstVariant.price,
        quantity: 1,
        selectedOptions: firstVariant.selectedOptions || [],
      });
      
      toast.success('Aggiunto al carrello', {
        description: node.title,
        position: 'top-center',
      });
    };

    return (
      <motion.div
        ref={ref}
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.5, delay: index * 0.05 }}
        className="group"
      >
        <Link to={`/product/${node.handle}`} className="block">
          <div className="relative overflow-hidden rounded-lg bg-card mb-3">
            <div className="aspect-square overflow-hidden">
              {firstImage ? (
                <img
                  src={firstImage.url.includes('cdn.shopify.com') ? `${firstImage.url}${firstImage.url.includes('?') ? '&' : '?'}width=400` : firstImage.url}
                  alt={firstImage.altText || `${node.title} - Handcrafted bronze jewelry by Sampaoli`}
                  className="w-full h-full object-cover img-zoom"
                  loading="lazy"
                  width={400}
                  height={400}
                />
              ) : (
                <div className="w-full h-full bg-secondary flex items-center justify-center">
                  <span className="text-muted-foreground text-sm">No image</span>
                </div>
              )}
            </div>
            
            {/* Quick add button */}
            <div className="absolute bottom-0 left-0 right-0 p-3 bg-gradient-to-t from-charcoal/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
              <Button
                variant="secondary"
                size="sm"
                className="w-full bg-ivory text-charcoal hover:bg-cream"
                onClick={handleAddToCart}
                disabled={isLoading}
              >
                {isLoading ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <ShoppingCart className="w-4 h-4 mr-2" />
                )}
                {t('common.addToCart')}
              </Button>
            </div>
          </div>
          
          <h3 className="font-heading text-sm md:text-base font-medium text-foreground mb-1 group-hover:text-bronze transition-colors line-clamp-2">
            {node.title}
          </h3>
          <p className="font-body text-sm text-muted-foreground">
            {formatPrice(price.amount, price.currencyCode)}
          </p>
        </Link>
      </motion.div>
    );
  }
);

ProductCard.displayName = 'ProductCard';
